import * as React from 'react';
import classnames from 'classnames';
import { createRecordUrl, formatDecimal, formatMoney, formatPercent } from 'utils/formatter';
import { useSelector } from 'react-redux';
import { artikelGruppenByNrSelector, einheitenByCodeSelector } from 'store/lookups/selector';
import {
    LieferantenrechnungPosition,
    defaultLieferantenrechnungPosition,
    moveUpLieferantenrechnungPosition,
} from 'api/lieferantenrechnungPosition';
import { EditIcon, DeleteIcon, UpIcon } from '@fluentui/react-icons';
import { Kunde } from 'api/kunde';
import { formStyles } from 'styles/formStyles';
import { OpenLink } from 'formComponents/OpenLink/OpenLink';
import { getArtikelDetailId } from 'utils/helper';
import { getTheme, ITheme, mergeStyleSets } from '@fluentui/react/lib/Styling';
import {
    LieferantenrechnungAraPosition,
    useLieferantenrechnungAraPositionList,
} from 'api/lieferantenrechnungAraPosition';
import { LieferantenrechnungPositionEdit } from './LieferantenrechnungPositionEdit';
import { LieferantenrechnungPositionDelete } from './LieferantenrechnungPositionDelete';
import { LieferantenrechnungPositionenHeader } from './LieferantenrechnungPositionenHeader';
import { LieferantenrechnungAraPositionEdit } from './LieferantenrechnungAraPositionEdit';

const theme: ITheme = getTheme();
const { palette } = theme;

const styles = mergeStyleSets({
    selected: {
        background: palette.neutralTertiary + ' !important',
    },
    selectable: {
        selectors: {
            '&:hover': { background: palette.neutralLight },
        },
    },
});

interface LieferantenrechnungPositionenProps {
    lieferant: Kunde;
    rNr: string;
    lieferantenrechnungPositionen: LieferantenrechnungPosition[];
    viewMode: 'short' | 'long';
    withHeader?: boolean;
    allowEdit?: boolean;
    refetch?: () => void;
}

export const LieferantenrechnungPositionen: React.FC<LieferantenrechnungPositionenProps> = ({
    lieferant,
    rNr,
    lieferantenrechnungPositionen,
    withHeader = true,
    allowEdit = false,
    refetch,
}) => {
    const artikelGruppenByNr = useSelector(artikelGruppenByNrSelector);
    const einheitenByCode = useSelector(einheitenByCodeSelector);

    const [
        lieferantenrechnungPositionEditData,
        setLieferantenrechnungPositionEditData,
    ] = React.useState<LieferantenrechnungPosition | null>(null);
    const [
        lieferantenrechnungPositionDeleteData,
        setLieferantenrechnungPositionDeleteData,
    ] = React.useState<LieferantenrechnungPosition | null>(null);

    const [selectedPos, setSelectedPos] = React.useState<number | null>(null);

    const [
        lieferantenrechnungAraPositionEditData,
        setLieferantenrechnungAraPositionEditData,
    ] = React.useState<LieferantenrechnungAraPosition | null>(null);

    const lieferantenrechnungAraPositionList = useLieferantenrechnungAraPositionList(
        {
            rNr,
            rPos: selectedPos || 0,
        },
        { enabled: selectedPos !== null },
    );

    return (
        <>
            <table className={formStyles.positionenTable}>
                {withHeader && (
                    <LieferantenrechnungPositionenHeader
                        allowEdit={allowEdit}
                        onAdd={() =>
                            setLieferantenrechnungPositionEditData({
                                ...defaultLieferantenrechnungPosition,
                                rNr,
                                pos: -1,
                            })
                        }
                    />
                )}
                <tbody>
                    {lieferantenrechnungPositionen.map((lieferantenrechnungPosition) => (
                        <tr
                            key={lieferantenrechnungPosition.pos}
                            className={classnames(formStyles.positionenRow, styles.selectable, {
                                [styles.selected]: lieferantenrechnungPosition.pos === selectedPos,
                            })}
                            onClick={() => setSelectedPos(lieferantenrechnungPosition.pos)}
                        >
                            <td className={classnames(formStyles.positionenCell, formStyles.positionenNrCell)}>
                                {lieferantenrechnungPosition.pos}
                            </td>
                            <td
                                className={classnames(
                                    formStyles.positionenCell,
                                    formStyles.positionenArtikelGruppeCell,
                                )}
                            >
                                {(artikelGruppenByNr[lieferantenrechnungPosition.artikelGruppeNr] &&
                                    artikelGruppenByNr[lieferantenrechnungPosition.artikelGruppeNr]?.text) ||
                                    lieferantenrechnungPosition.artikelGruppeNr}
                            </td>
                            <td className={classnames(formStyles.positionenCell, formStyles.positionenArtikelNrCell)}>
                                <span>
                                    {lieferantenrechnungPosition.artikelGruppeNr &&
                                    lieferantenrechnungPosition.artikelNr ? (
                                        <OpenLink
                                            link={createRecordUrl(
                                                'lager/artikel',
                                                undefined,
                                                getArtikelDetailId(
                                                    lieferantenrechnungPosition.artikelGruppeNr,
                                                    lieferantenrechnungPosition.artikelNr,
                                                ),
                                                undefined,
                                                false,
                                            )}
                                            label={lieferantenrechnungPosition.artikelNr}
                                        />
                                    ) : (
                                        ''
                                    )}
                                </span>
                            </td>
                            <td className={classnames(formStyles.positionenCell, formStyles.positionenTextCell)}>
                                {lieferantenrechnungPosition.posText}
                                {lieferantenrechnungPosition.posBem ? (
                                    <i>
                                        <br />
                                        {lieferantenrechnungPosition.posBem}
                                    </i>
                                ) : (
                                    ''
                                )}
                            </td>
                            <td className={classnames(formStyles.positionenCell, formStyles.positionenEinhCell)}>
                                {lieferantenrechnungPosition.posEinh
                                    ? einheitenByCode[lieferantenrechnungPosition.posEinh].text ||
                                      lieferantenrechnungPosition.posEinh
                                    : '?'}
                            </td>
                            <td className={classnames(formStyles.positionenCell, formStyles.positionenMengeCell)}>
                                {formatDecimal(lieferantenrechnungPosition.posMenge, 3)}
                            </td>
                            <td className={classnames(formStyles.positionenCell, formStyles.positionenMoneyCell)}>
                                {formatMoney(lieferantenrechnungPosition.posEPreis, 3)}
                            </td>
                            {/*
                            <td className={classnames(formStyles.positionenCell, formStyles.positionenMoneyCell)}>
                                {formatMoney(rechnungPosition.posEPreis, 3)}
                            </td>
                            */}
                            <td
                                className={classnames(formStyles.positionenCell, formStyles.positionenMoneyCell)}
                                title={'VK-Brutto: ' + formatMoney(lieferantenrechnungPosition.posBrutto)}
                            >
                                {formatMoney(lieferantenrechnungPosition.posNetto)}
                            </td>
                            {/*
                            <td className={classnames(formStyles.positionenCell, formStyles.positionenMoneyCell)}>
                                {formatMoney(rechnungPosition.posBrutto)}
                            </td>
                            */}
                            <td
                                className={classnames(formStyles.positionenCell, formStyles.positionenMwstCell)}
                                title={'VK-Brutto: ' + formatMoney(lieferantenrechnungPosition.posBrutto)}
                            >
                                {formatPercent(lieferantenrechnungPosition.posMwst)}
                            </td>
                            {allowEdit && (
                                <>
                                    <td
                                        className={classnames(
                                            formStyles.positionenCell,
                                            formStyles.positionenActionCell,
                                        )}
                                    >
                                        {lieferantenrechnungPosition.pos > 1 && (
                                            <UpIcon
                                                className={formStyles.positionenAction}
                                                onClick={async () => {
                                                    await moveUpLieferantenrechnungPosition(
                                                        lieferantenrechnungPosition.rNr,
                                                        lieferantenrechnungPosition.pos,
                                                    );
                                                    setSelectedPos(null);
                                                    if (refetch) {
                                                        refetch();
                                                    }
                                                }}
                                            />
                                        )}
                                    </td>
                                    <td
                                        className={classnames(
                                            formStyles.positionenCell,
                                            formStyles.positionenActionCell,
                                        )}
                                    >
                                        <EditIcon
                                            className={formStyles.positionenAction}
                                            onClick={() =>
                                                setLieferantenrechnungPositionEditData(lieferantenrechnungPosition)
                                            }
                                        />
                                    </td>
                                    <td
                                        className={classnames(
                                            formStyles.positionenCell,
                                            formStyles.positionenActionCell,
                                        )}
                                    >
                                        <DeleteIcon
                                            className={formStyles.positionenAction}
                                            onClick={() =>
                                                setLieferantenrechnungPositionDeleteData(lieferantenrechnungPosition)
                                            }
                                        />
                                    </td>
                                </>
                            )}
                        </tr>
                    ))}
                </tbody>
            </table>
            {lieferantenrechnungAraPositionList.data && (
                <table className={formStyles.positionenTable}>
                    {withHeader && <LieferantenrechnungPositionenHeader allowEdit={allowEdit} />}
                    <tbody>
                        {lieferantenrechnungAraPositionList.data.map((lieferantenrechnungAraPosition) => (
                            <tr key={lieferantenrechnungAraPosition.pos} className={formStyles.positionenRow}>
                                <td className={classnames(formStyles.positionenCell, formStyles.positionenNrCell)}>
                                    {lieferantenrechnungAraPosition.pos}
                                </td>
                                <td
                                    className={classnames(
                                        formStyles.positionenCell,
                                        formStyles.positionenArtikelGruppeCell,
                                    )}
                                >
                                    {(artikelGruppenByNr[lieferantenrechnungAraPosition.artikelGruppeNr] &&
                                        artikelGruppenByNr[lieferantenrechnungAraPosition.artikelGruppeNr]?.text) ||
                                        lieferantenrechnungAraPosition.artikelGruppeNr}
                                </td>
                                <td
                                    className={classnames(
                                        formStyles.positionenCell,
                                        formStyles.positionenArtikelNrCell,
                                    )}
                                >
                                    <span>
                                        {lieferantenrechnungAraPosition.artikelGruppeNr &&
                                        lieferantenrechnungAraPosition.artikelNr ? (
                                            <OpenLink
                                                link={createRecordUrl(
                                                    'lager/artikel',
                                                    undefined,
                                                    getArtikelDetailId(
                                                        lieferantenrechnungAraPosition.artikelGruppeNr,
                                                        lieferantenrechnungAraPosition.artikelNr,
                                                    ),
                                                    undefined,
                                                    false,
                                                )}
                                                label={lieferantenrechnungAraPosition.artikelNr}
                                            />
                                        ) : (
                                            ''
                                        )}
                                    </span>
                                </td>
                                <td className={classnames(formStyles.positionenCell, formStyles.positionenTextCell)}>
                                    {lieferantenrechnungAraPosition.posText}
                                    {lieferantenrechnungAraPosition.posBem ? (
                                        <i>
                                            <br />
                                            {lieferantenrechnungAraPosition.posBem}
                                        </i>
                                    ) : (
                                        ''
                                    )}
                                </td>
                                <td className={classnames(formStyles.positionenCell, formStyles.positionenEinhCell)}>
                                    {lieferantenrechnungAraPosition.posEinh
                                        ? einheitenByCode[lieferantenrechnungAraPosition.posEinh].text ||
                                          lieferantenrechnungAraPosition.posEinh
                                        : '?'}
                                </td>
                                <td className={classnames(formStyles.positionenCell, formStyles.positionenMengeCell)}>
                                    {formatDecimal(lieferantenrechnungAraPosition.posMenge, 3)}
                                </td>
                                <td className={classnames(formStyles.positionenCell, formStyles.positionenMoneyCell)}>
                                    {formatMoney(lieferantenrechnungAraPosition.posEPreis, 3)}
                                </td>
                                <td
                                    className={classnames(formStyles.positionenCell, formStyles.positionenMoneyCell)}
                                    title={'VK-Brutto: ' + formatMoney(lieferantenrechnungAraPosition.posBrutto)}
                                >
                                    {formatMoney(lieferantenrechnungAraPosition.posNetto)}
                                </td>
                                <td
                                    className={classnames(formStyles.positionenCell, formStyles.positionenMwstCell)}
                                    title={'VK-Brutto: ' + formatMoney(lieferantenrechnungAraPosition.posBrutto)}
                                >
                                    {formatPercent(lieferantenrechnungAraPosition.posMwst)}
                                </td>
                                {allowEdit && (
                                    <>
                                        <td
                                            className={classnames(
                                                formStyles.positionenCell,
                                                formStyles.positionenActionCell,
                                            )}
                                        />
                                        <td
                                            className={classnames(
                                                formStyles.positionenCell,
                                                formStyles.positionenActionCell,
                                            )}
                                        >
                                            <EditIcon
                                                className={formStyles.positionenAction}
                                                onClick={() =>
                                                    setLieferantenrechnungAraPositionEditData(
                                                        lieferantenrechnungAraPosition,
                                                    )
                                                }
                                            />
                                        </td>
                                    </>
                                )}
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
            {lieferantenrechnungPositionEditData !== null && (
                <LieferantenrechnungPositionEdit
                    kunde={lieferant}
                    rNr={lieferantenrechnungPositionEditData.rNr}
                    pos={lieferantenrechnungPositionEditData.pos}
                    onClose={(_refetch) => {
                        setLieferantenrechnungPositionEditData(null);
                        if (_refetch && refetch) {
                            refetch();
                            lieferantenrechnungAraPositionList.refetch();
                        }
                    }}
                />
            )}
            {lieferantenrechnungPositionDeleteData !== null && (
                <LieferantenrechnungPositionDelete
                    lieferantenrechnungPosition={lieferantenrechnungPositionDeleteData}
                    onClose={(_refetch) => {
                        setLieferantenrechnungPositionDeleteData(null);
                        if (_refetch && refetch) {
                            refetch();
                            lieferantenrechnungAraPositionList.refetch();
                        }
                    }}
                />
            )}
            {lieferantenrechnungAraPositionEditData !== null && (
                <LieferantenrechnungAraPositionEdit
                    kunde={lieferant}
                    rNr={lieferantenrechnungAraPositionEditData.rNr}
                    rPos={lieferantenrechnungAraPositionEditData.rPos}
                    pos={lieferantenrechnungAraPositionEditData.pos}
                    onClose={(_refetch) => {
                        setLieferantenrechnungAraPositionEditData(null);
                        if (_refetch && refetch) {
                            refetch();
                            lieferantenrechnungAraPositionList.refetch();
                        }
                    }}
                />
            )}
        </>
    );
};
